import Axios from "axios";
import db from "../../database";
import useAuthStore from "../../stores/useAuthStore";
import { resolveGiftCardPrintType, getTillTemplateXml, describeTillTemplates, describePrintLine, templateFormat, safeTrace } from "./giftCardPrintType";
import { notifyTemplateNotConfigured, PRINT_TEMPLATES } from "./printTemplateErrors";
import { printTag } from "./printFailureNotice";

const printGiftCardHC = async (printData, requestedType) => {
  const tillData = useAuthStore.getState().tillData;
  const printerURL = tillData?.tillAccess?.cwrTill?.hardwareController?.imageUrl;
  let templateSource = "till-level";
  let legacyPick = null;

  safeTrace("OB print start", { requestedType, line: describePrintLine(printData), tillTemplatesHeldByApp: describeTillTemplates(tillData?.tillAccess?.cwrTill) });

  // TSK-4186: till-level Gift Card / Credit Note / Voucher template, once configured in back office.
  // A Voucher / Credit Note-group card is resolved from its gift card type's group so it no longer prints with the Gift Card template.
  const type = await resolveGiftCardPrintType(printData, requestedType);
  let GiftCardHCPrintPayload = getTillTemplateXml(tillData?.tillAccess?.cwrTill, type);
  const tillTemplateFormat = templateFormat(GiftCardHCPrintPayload);
  if (GiftCardHCPrintPayload && !GiftCardHCPrintPayload.trim().startsWith("<")) {
    // e.g. a JSON template assigned to an OB (XML) till -- log it and fall back instead of sending it to the printer
    console.error("[GiftCardPrint] till template is not XML for the OB controller, using the legacy template");
    safeTrace("OB till template unusable, falling back to legacy", { type, format: tillTemplateFormat });
    GiftCardHCPrintPayload = null;
  }

  if (!GiftCardHCPrintPayload) {
    // Till-level template not configured yet -- fall back to the legacy
    // shared "DIG" giftCardData template so existing tills keep printing.
    let giftCardData = await db.giftCardData?.toArray();
    giftCardData?.map((GCData) => {
      if (GCData?.type === "DIG") {
        GiftCardHCPrintPayload = GCData?.printTemplate?.xmlcode2;
        legacyPick = { typeId: GCData?.cwrGiftcardTypeId, typeName: GCData?.name, group: GCData?.giftCardGroup, template: GCData?.printTemplate?.name || null };
      }
    });
    templateSource = "legacy-shared-DIG";
  }
  safeTrace("OB template chosen", { requestedType, resolvedType: type, source: templateSource, tillTemplateFormat, legacyPick, payloadFormat: templateFormat(GiftCardHCPrintPayload), printerURL });
  if (!GiftCardHCPrintPayload) {
    notifyTemplateNotConfigured(PRINT_TEMPLATES.giftCard, type);
    return;
  }
  GiftCardHCPrintPayload = GiftCardHCPrintPayload.replaceAll("@@giftCardName@@", `${type === "Credit Note" ? "Credit Note" : printData?.name}`);
  GiftCardHCPrintPayload = GiftCardHCPrintPayload.replaceAll("@@giftCardNumber@@", `${printData?.cardNo}`);
  GiftCardHCPrintPayload = GiftCardHCPrintPayload.replaceAll("@@giftCardAmount@@", `${printData?.nettotal} `);
  GiftCardHCPrintPayload = GiftCardHCPrintPayload.replaceAll("@@validThrough@@", `${printData?.expiryGiftCard} `);

  Axios.post(`${printerURL}printer`, GiftCardHCPrintPayload, {
    headers: {
      "Content-Type": "application/xml; charset=UTF-8",
      Accept: "application/xml",
    },
    ...printTag("giftCard", printData?.cardNo),
  })
    .then((res) => {
      safeTrace("OB print request sent", { resolvedType: type, status: res?.status ?? null });
    })
    .catch((error) => {
      safeTrace("OB print request FAILED", { resolvedType: type, printerURL, error: String(error?.message || error), status: error?.response?.status ?? null });
    });
};

// Traced wrapper: a failure is recorded and re-thrown exactly as before (callers do not await this).
const GiftCardHCPrint = async (printData, requestedType) => {
  try {
    return await printGiftCardHC(printData, requestedType);
  } catch (error) {
    safeTrace("OB print threw", { requestedType, line: describePrintLine(printData), error: String(error?.message || error) });
    throw error;
  }
};

export default GiftCardHCPrint;
